import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import Plotly from 'plotly.js-cartesian-dist-min';
import { DocumentArrowDownIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import FieldExportButton from './FieldExportButton';

// --- Unit Conversion Constants ---
// Worker output is in standard units: IRI in in/mi, elevation in inches.
// These factors only apply when the user toggles to metric display.
const IN_PER_MI_TO_M_PER_KM = 1 / 63.3596;   // in/mi → m/km
const INCHES_TO_MILLIMETERS = 25.4;          // in → mm

// --- Helpers ---

const formatStation = (distance) => {
  if (distance === null || distance === undefined) return "0+00";
  const hundreds = Math.floor(distance / 100);
  const remainder = distance % 100;
  return `${hundreds}+${remainder.toFixed(0).padStart(2, '0')}`;
};

// UPDATED: Robust Binary Search that handles Ascending OR Descending data
const findClosestIndex = (profile, targetStn) => {
  if (!profile || profile.length === 0) return -1;
  const isObjectArray = profile[0] && typeof profile[0] === 'object' && 'station' in profile[0];
  const getVal = (i) => isObjectArray ? profile[i].station : profile[i];
  
  const len = profile.length;
  const startVal = getVal(0);
  const endVal = getVal(len - 1);
  const isDescending = startVal > endVal;

  // 1. Handle Boundaries based on direction
  if (isDescending) {
     // Target > Max (Start) -> Index 0
     if (targetStn >= startVal) return 0;
     // Target < Min (End) -> Index Last
     if (targetStn <= endVal) return len - 1;
  } else {
     // Standard Ascending
     if (targetStn <= startVal) return 0;
     if (targetStn >= endVal) return len - 1;
  }

  // 2. Binary Search adapted for direction
  let left = 0;
  let right = len - 1;
  while (left <= right) {
    const mid = (left + right) >>> 1;
    const val = getVal(mid);
    
    if (val === targetStn) return mid;

    if (isDescending) {
       // Descending: Smaller values are to the right
       if (val < targetStn) right = mid - 1; else left = mid + 1;
    } else {
       // Ascending: Larger values are to the right
       if (val < targetStn) left = mid + 1; else right = mid - 1;
    }
  }

  // 3. Return closest of the two pointers
  const pLeft = getVal(right);
  const pRight = getVal(left);
  if (pLeft === undefined) return left;
  if (pRight === undefined) return right;
  return (Math.abs(targetStn - pLeft) < Math.abs(targetStn - pRight)) ? right : left;
};

const getVisibleYStats = (traces, xStart, xEnd) => {
  let min = Infinity;
  let max = -Infinity;
  let hasData = false;

  for (let t = 0; t < traces.length; t++) {
    const trace = traces[t];
    if (!trace || !trace.x || !trace.y || trace.x.length === 0) continue;
    if (trace.fill === 'toself') continue; 

    const startIndex = findClosestIndex(trace.x, xStart);
    const endIndex = findClosestIndex(trace.x, xEnd);
    if (startIndex === -1 || endIndex === -1) continue;

    // UPDATED: Ensure loop runs low->high index regardless of sort order
    // In descending data, startIndex (Min Stn) will be > endIndex (Max Stn)
    const i1 = Math.min(startIndex, endIndex);
    const i2 = Math.max(startIndex, endIndex);

    const start = Math.max(0, i1);
    const end = Math.min(trace.x.length - 1, i2 + 1); // +1 for safety margin

    for (let i = start; i <= end; i++) {
      const yVal = trace.y[i];
      if (yVal !== null && yVal !== undefined && !isNaN(yVal) && isFinite(yVal)) {
         if (yVal < min) min = yVal;
         if (yVal > max) max = yVal;
         hasData = true;
      }
    }
  }
  return hasData ? { min, max } : null;
};

const prepareDataArrays = (data, valueKey, labelKey, unit, factor, ignored, hide, xRangeDiff) => {
  if (!data || data.length === 0) return { x: [], y: [], text: [] };
  let step = 1;
  if (xRangeDiff > 100000) step = 5;
  else if (xRangeDiff > 50000) step = 2;

  const x = [];
  const y = [];
  const text = []; 
  const hasIgnored = ignored && ignored.length > 0;
  let wasIgnored = false;

  for (let i = 0; i < data.length; i += step) {
    const d = data[i];
    if (hide && hasIgnored) {
       const isIgnored = ignored.some(seg => d.station >= seg.from && d.station <= seg.to);
       if (isIgnored) {
         wasIgnored = true;
         continue;
       }
    }
    if (wasIgnored && x.length > 0) {
      x.push(null);
      y.push(null);
      text.push(null);
    }
    x.push(d.station);
    y.push(d[valueKey] * factor);
    
    const valLabel = (d[valueKey] * factor).toFixed(2);
    text.push(`<b>${labelKey}</b>: ${valLabel} ${unit}`);
    
    wasIgnored = false;
  }
  return { x, y, text };
};

const applyHighPassFilter = (profile, wavelengthFt) => {
  if (!profile || profile.length === 0) return [];
  const prefixSum = new Float64Array(profile.length);
  let currentSum = 0;
  for (let i = 0; i < profile.length; i++) {
    currentSum += profile[i].elevation;
    prefixSum[i] = currentSum;
  }
  const result = [];
  const halfWindow = wavelengthFt / 2;
  const n = profile.length;
  let leftIdx = 0;
  let rightIdx = 0;
  for (let i = 0; i < n; i++) {
    const currentStation = profile[i].station;
    const windowStart = currentStation - halfWindow;
    const windowEnd = currentStation + halfWindow;
    while (leftIdx < n - 1 && profile[leftIdx].station < windowStart) leftIdx++;
    while (rightIdx < n - 1 && profile[rightIdx + 1].station <= windowEnd) rightIdx++;
    const count = rightIdx - leftIdx + 1;
    if (count > 0) {
      const sumRight = prefixSum[rightIdx];
      const sumLeft = leftIdx > 0 ? prefixSum[leftIdx - 1] : 0;
      const average = (sumRight - sumLeft) / count;
      result.push({ ...profile[i], texture: profile[i].elevation - average });
    } else {
      result.push({ ...profile[i], texture: 0 });
    }
  }
  return result;
};

const generateStationTicks = (min, max) => {
  const range = max - min;
  if (range <= 0) return { vals: [], text: [] };

  const targetTicks = 20; 
  const rawStep = range / targetTicks;
  
  const intervals = [10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 5000];
  let step = intervals[0];
  
  for (let i = 0; i < intervals.length; i++) {
    if (intervals[i] >= rawStep) {
      step = intervals[i];
      break;
    }
    step = intervals[intervals.length - 1];
  }
  
  const start = Math.ceil(min / step) * step;
  const vals = [];
  const text = [];

  for (let v = start; v <= max; v += step) {
    vals.push(v);
    text.push(formatStation(v));
  }

  return { vals, text };
};

const exportToCSV = (data, filename, units = 'metric', conversionFactors = {}) => {
  if (!data || data.length === 0) return;
  const { iriFactor = 1, elevFactor = 1 } = conversionFactors;
  const convertedData = data.map(row => {
    const converted = { ...row };
    if (row.leftIRI !== undefined) converted.leftIRI = (row.leftIRI * iriFactor).toFixed(2);
    if (row.rightIRI !== undefined) converted.rightIRI = (row.rightIRI * iriFactor).toFixed(2);
    if (row.avgIRI !== undefined) converted.avgIRI = (row.avgIRI * iriFactor).toFixed(2);
    if (row.leftElevation !== undefined) converted.leftElevation = (row.leftElevation * elevFactor).toFixed(4);
    if (row.rightElevation !== undefined) converted.rightElevation = (row.rightElevation * elevFactor).toFixed(4);
    delete converted.stationLabel;
    return converted;
  });
  const isStandard = units === 'standard';
  const iriUnit = isStandard ? 'in/mi' : 'm/km';
  const elevUnit = isStandard ? 'in' : 'mm';
  const headers = Object.keys(convertedData[0]);
  const headerRow = headers.map(header => {
    if (header.includes('IRI')) return `${header} (${iriUnit})`;
    if (header.includes('Elevation')) return `${header} (${elevUnit})`;
    return header;
  }).join(',');
  const csvRows = [
    headerRow,
    ...convertedData.map(row => 
      headers.map(fieldName => {
        const value = row[fieldName];
        return (value === null || value === undefined) ? '' : value;
      }).join(',')
    )
  ];
  const blob = new Blob([csvRows.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// --- Fast Chart Component ---
const FastPlotlyChart = ({ divId, data, layout, config, onRelayout, setPlotInstance }) => {
  const graphDiv = useRef(null);
  const isMounted = useRef(false);

  useEffect(() => {
    if (!graphDiv.current) return;
    Plotly.newPlot(graphDiv.current, data, layout, config).then((p) => {
      isMounted.current = true;
      if (setPlotInstance) setPlotInstance(p);
      graphDiv.current.on('plotly_relayout', (eventData) => {
        if (onRelayout) onRelayout(eventData);
      });
    });
    return () => {
      if (graphDiv.current) {
        Plotly.purge(graphDiv.current);
        isMounted.current = false;
      }
    };
  }, []);

  useEffect(() => {
    if (isMounted.current && graphDiv.current) {
      Plotly.react(graphDiv.current, data, layout, config);
    }
  }, [data, layout, config]);

  return <div id={divId} ref={graphDiv} style={{ width: '100%', height: '100%' }} />;
};


// --- Main Component ---

const KmlCharts = ({
  iriProfileData,
  elevationProfileData,
  thresholds,
  kmlLoading,
  kmlError,
  sessionData,
  alrDefects = [],
  allSegments = [],
  surfaceEvents = [],
  isReversed = false
}) => {
  const [units, setUnits] = useState('standard');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hideIgnoredData, setHideIgnoredData] = useState(true);
  const [showAlrRegions, setShowAlrRegions] = useState(true);
  const [showSurfaceEvents, setShowSurfaceEvents] = useState(true);
  const [wheelpathVisibility, setWheelpathVisibility] = useState({ left: true, right: true, avg: false });
  const [xAxisRange, setXAxisRange] = useState(null); 
  
  const iriChartRef = useRef(null);
  const elevChartRef = useRef(null);
  const throttleRef = useRef(null);

  const ignoredSegments = useMemo(() => (allSegments || []).filter(s => s.exception).map(s => ({ from: s.from_station_ft, to: s.to_station_ft })), [allSegments]);
  
  // Parse floats for safety
  const analysisStart = parseFloat(sessionData?.analysis_start_station ?? sessionData?.start_station_ft ?? 0);
  const analysisEnd = parseFloat(sessionData?.analysis_end_station ?? sessionData?.end_station_ft ?? 0);
  
  // Calculate global Min/Max Station for robust Tick generation
  const minStation = Math.min(analysisStart, analysisEnd);
  const maxStation = Math.max(analysisStart, analysisEnd);

  const cutoffWavelength = parseFloat(sessionData?.meta?.['high-pass_filter(ft.)']) || 300;
  
  const isStandard = units === 'standard';
  // Worker outputs IRI in in/mi and elevation in inches. Standard view passes
  // them through; metric view converts to m/km and mm respectively.
  const iriFactor = isStandard ? 1.0 : IN_PER_MI_TO_M_PER_KM;
  const elevFactor = isStandard ? 1.0 : INCHES_TO_MILLIMETERS;
  const iriUnit = isStandard ? 'in/mi' : 'm/km';
  const elevUnit = isStandard ? 'in' : 'mm';
  const thresholdVal = isStandard ? thresholds.active : thresholds.active * IN_PER_MI_TO_M_PER_KM;

  // Reset zoom when session changes
  useEffect(() => {
    setXAxisRange(null);
  }, [sessionData?.id, analysisStart, analysisEnd]);

  const filteredElev = useMemo(() => {
    if (!elevationProfileData) return null;
    return {
      left: applyHighPassFilter(elevationProfileData.leftProfile, cutoffWavelength),
      right: applyHighPassFilter(elevationProfileData.rightProfile, cutoffWavelength)
    };
  }, [elevationProfileData, cutoffWavelength]);

  const combinedCSVData = useMemo(() => {
    if (!iriProfileData || !iriProfileData.mergedData) return [];
    return iriProfileData.mergedData.map((iri, i) => {
      let leftElev = null, rightElev = null;
      if (filteredElev && filteredElev.left && filteredElev.left.length > i) {
        leftElev = filteredElev.left[i].texture;
        rightElev = filteredElev.right[i].texture;
      }
      return {
        station: iri.station,
        stationLabel: iri.stationLabel,
        leftIRI: iri.leftIRI,
        rightIRI: iri.rightIRI,
        avgIRI: iri.avgIRI,
        leftElevation: leftElev,
        rightElevation: rightElev,
      };
    });
  }, [iriProfileData, filteredElev]);


  // --- Prepare Traces ---
  const iriData = useMemo(() => {
    if (!iriProfileData) return [];
    const traces = [];
    
    const common = { 
      type: 'scattergl', 
      mode: 'lines', 
      hoverinfo: 'text',
      hovertemplate: '%{text}<extra></extra>' 
    }; 
    
    const xDiff = xAxisRange 
      ? Math.abs(xAxisRange[1] - xAxisRange[0]) 
      : Math.abs(maxStation - minStation);

    // 1. ALR Fills & Annotations
    if (showAlrRegions && alrDefects.length > 0) {
      const processALR = (wpKey, color, textColor) => {
        const visKey = wpKey.toLowerCase();
        if (!wheelpathVisibility[visKey]) return;
        const rawSource = wpKey === 'Left' ? iriProfileData.leftWheelpath : iriProfileData.rightWheelpath;
        if(!rawSource) return;

        const defects = alrDefects.filter(d => d.wheelpath === wpKey);
        
        defects.forEach(alr => {
           const s = alr.startDistance || alr.fromStation;
           const e = alr.stopDistance || alr.toStation;
           const sIdx = findClosestIndex(rawSource, s);
           const eIdx = findClosestIndex(rawSource, e);
           const subsetX = [];
           const subsetY = [];
           
           const i1 = Math.min(sIdx, eIdx);
           const i2 = Math.max(sIdx, eIdx);
           const startI = Math.max(0, i1);
           const endI = Math.min(rawSource.length - 1, i2 + 1);

           for(let i = startI; i <= endI; i++) {
             const valX = rawSource[i].station;
             const valY = rawSource[i].iri * iriFactor;
             if (valX !== null && valY !== null && !isNaN(valY)) {
               subsetX.push(valX);
               subsetY.push(valY);
             }
           }
           
           if(subsetX.length > 0) {
             traces.push({
               x: [subsetX[0], ...subsetX, subsetX[subsetX.length-1], subsetX[0]],
               y: [thresholdVal, ...subsetY, thresholdVal, thresholdVal],
               fill: 'toself',
               fillcolor: color,
               line: { width: 0 },
               mode: 'lines',
               type: 'scatter',
               hoverinfo: 'skip',
               showlegend: false
             });
             
             let maxY = -1;
             let peakStation = s;
             for(let i=0; i < subsetY.length; i++) {
                 if(subsetY[i] > maxY) {
                     maxY = subsetY[i];
                     peakStation = subsetX[i];
                 }
             }

             traces.push({
               x: [peakStation],
               y: [maxY * 1.05], 
               text: [`ALR @ ${formatStation(peakStation)}`],
               mode: 'text',
               type: 'scattergl', 
               textposition: 'top center',
               textfont: { family: 'sans-serif', size: 10, color: textColor },
               hoverinfo: 'skip',
               showlegend: false
             });
           }
        });
      };

      processALR('Left', 'rgba(16, 185, 129, 0.25)', '#10b981'); 
      processALR('Right', 'rgba(59, 130, 246, 0.25)', '#3b82f6'); 
    }

    // 2. Main Profile Lines
    if (wheelpathVisibility.left) {
      const d = prepareDataArrays(iriProfileData.leftWheelpath, 'iri', 'Left IRI', iriUnit, iriFactor, ignoredSegments, hideIgnoredData, xDiff);
      traces.push({ ...common, x: d.x, y: d.y, text: d.text, name: 'Left IRI', line: { color: '#10b981', width: 1.5 } });
    }
    if (wheelpathVisibility.right) {
      const d = prepareDataArrays(iriProfileData.rightWheelpath, 'iri', 'Right IRI', iriUnit, iriFactor, ignoredSegments, hideIgnoredData, xDiff);
      traces.push({ ...common, x: d.x, y: d.y, text: d.text, name: 'Right IRI', line: { color: '#3b82f6', width: 1.5 } });
    }
    if (wheelpathVisibility.avg) {
      const d = prepareDataArrays(iriProfileData.mergedData, 'avgIRI', 'Avg IRI', iriUnit, iriFactor, ignoredSegments, hideIgnoredData, xDiff);
      traces.push({ ...common, x: d.x, y: d.y, text: d.text, name: 'Avg IRI', line: { color: '#9333ea', width: 2 } });
    }
    
    // 3. Threshold Line
    traces.push({
      x: [minStation, maxStation], y: [thresholdVal, thresholdVal],
      type: 'scattergl', mode: 'lines', line: { color: '#ef4444', width: 2, dash: 'dash' }, hoverinfo: 'skip'
    });

    return traces;
  }, [iriProfileData, wheelpathVisibility, units, hideIgnoredData, showAlrRegions, alrDefects, minStation, maxStation, thresholdVal, xAxisRange]);

  const elevData = useMemo(() => {
    if (!filteredElev) return [];
    const traces = [];
    const common = { 
      type: 'scattergl', 
      mode: 'lines', 
      hoverinfo: 'text',
      hovertemplate: '%{text}<extra></extra>' 
    }; 
    const xDiff = xAxisRange 
      ? Math.abs(xAxisRange[1] - xAxisRange[0]) 
      : Math.abs(maxStation - minStation);

    // 1. Draw Main Profile Lines
    if (wheelpathVisibility.left && filteredElev.left) {
      const d = prepareDataArrays(filteredElev.left, 'texture', 'Left Elev', elevUnit, elevFactor, ignoredSegments, hideIgnoredData, xDiff);
      traces.push({ ...common, x: d.x, y: d.y, text: d.text, name: 'Left Elev', line: { color: '#ef4444', width: 1.5 } });
    }
    if (wheelpathVisibility.right && filteredElev.right) {
      const d = prepareDataArrays(filteredElev.right, 'texture', 'Right Elev', elevUnit, elevFactor, ignoredSegments, hideIgnoredData, xDiff);
      traces.push({ ...common, x: d.x, y: d.y, text: d.text, name: 'Right Elev', line: { color: '#f97316', width: 1.5 } });
    }

    // 2. Draw Bumps and Dips (Chord + Fill)
    if (showSurfaceEvents && surfaceEvents.length > 0) {
       surfaceEvents.forEach(e => {
         if (e.wheelpath === 'Left' && !wheelpathVisibility.left) return;
         if (e.wheelpath === 'Right' && !wheelpathVisibility.right) return;

         const profile = e.wheelpath === 'Left' ? filteredElev.left : filteredElev.right;
         if (!profile || profile.length === 0) return;

         const isBump = e.eventType === 'Bump';
         const baseColor = isBump ? '#ef4444' : '#3b82f6'; 
         const fillColor = isBump ? 'rgba(239, 68, 68, 0.25)' : 'rgba(59, 130, 246, 0.25)';
         
         const peakStn = e.peak_station ?? e.distance;
         if (peakStn === null || peakStn === undefined) return;

         const startStn = e.from_station ?? e.startDistance ?? e.from ?? (peakStn - 5);
         const endStn   = e.to_station ?? e.stopDistance ?? e.to ?? (peakStn + 5);

         const startIdx = findClosestIndex(profile, startStn);
         const endIdx = findClosestIndex(profile, endStn);
         const peakIdx = findClosestIndex(profile, peakStn);

         if (startIdx === -1 || endIdx === -1 || peakIdx === -1) return;
         if (!profile[startIdx] || !profile[endIdx] || !profile[peakIdx]) return;

         const i1 = Math.min(startIdx, endIdx);
         const i2 = Math.max(startIdx, endIdx);
         const sI = Math.max(0, i1);
         const eI = Math.min(profile.length - 1, i2 + 1);
         
         const segX = [];
         const segY = [];
         
         const yStart = (profile[startIdx].texture || 0) * elevFactor;
         const yEnd   = (profile[endIdx].texture || 0) * elevFactor;
         const yPeak  = (profile[peakIdx].texture || 0) * elevFactor;

         for(let i = sI; i <= eI; i++) {
            if (profile[i]) { 
                segX.push(profile[i].station);
                segY.push(profile[i].texture * elevFactor);
            }
         }

         traces.push({
           x: [profile[startIdx].station, profile[endIdx].station],
           y: [yStart, yEnd],
           mode: 'lines',
           line: { color: baseColor, width: 1, dash: 'dot' },
           hoverinfo: 'skip',
           showlegend: false,
           type: 'scattergl'
         });

         traces.push({
           x: segX,
           y: segY,
           mode: 'lines',
           line: { width: 0 }, 
           fill: 'tonexty',    
           fillcolor: fillColor,
           hoverinfo: 'skip',
           showlegend: false,
           type: 'scattergl'
         });

         traces.push({
           x: [profile[peakIdx].station],
           y: [yPeak],
           mode: 'text',
           text: [`${e.eventType}`],
           textposition: isBump ? 'top center' : 'bottom center',
           textfont: { family: 'sans-serif', size: 10, color: baseColor },
           hoverinfo: 'skip',
           showlegend: false,
           type: 'scattergl'
         });
       });
    }
    return traces;
  }, [filteredElev, wheelpathVisibility, units, showSurfaceEvents, surfaceEvents, xAxisRange, elevFactor, elevUnit, hideIgnoredData, ignoredSegments, minStation, maxStation]);

  // --- Dynamic Scaling & Ticks ---
  const iriYRange = useMemo(() => {
      const profileTraces = iriData.filter(t => t.name && (t.name.includes('IRI') || t.name.includes('Avg')));
      
      const currentXMin = xAxisRange ? Math.min(xAxisRange[0], xAxisRange[1]) : minStation;
      const currentXMax = xAxisRange ? Math.max(xAxisRange[0], xAxisRange[1]) : maxStation;
      
      const stats = getVisibleYStats(profileTraces, currentXMin, currentXMax);
      if (!stats) return [-1, 1];
      const { min, max } = stats;
      const finalMax = Math.max(max, thresholdVal);
      const range = finalMax - min;
      const padding = range === 0 ? 1.0 : range * 0.3; 
      return [Math.max(0, min - padding), finalMax + padding];
  }, [iriData, xAxisRange, minStation, maxStation, thresholdVal]);

  const elevYRange = useMemo(() => {
      const profileTraces = elevData.filter(t => t.name && t.name.includes('Elev'));
      
      const currentXMin = xAxisRange ? Math.min(xAxisRange[0], xAxisRange[1]) : minStation;
      const currentXMax = xAxisRange ? Math.max(xAxisRange[0], xAxisRange[1]) : maxStation;
      
      const stats = getVisibleYStats(profileTraces, currentXMin, currentXMax);
      if (!stats) return [-1, 1];
      const { min, max } = stats;
      const range = max - min;
      const padding = range === 0 ? 0.5 : range * 0.3;
      return [min - padding, max + padding];
  }, [elevData, xAxisRange, minStation, maxStation]);

  const xTicks = useMemo(() => {
    const min = xAxisRange ? Math.min(xAxisRange[0], xAxisRange[1]) : minStation;
    const max = xAxisRange ? Math.max(xAxisRange[0], xAxisRange[1]) : maxStation;
    return generateStationTicks(min, max);
  }, [xAxisRange, minStation, maxStation]);


  // --- Layouts ---
  const ignoredShapes = useMemo(() => 
    hideIgnoredData ? (ignoredSegments || []).map(seg => ({
      type: 'rect', xref: 'x', yref: 'paper',
      x0: seg.from, x1: seg.to, y0: 0, y1: 1,
      fillcolor: 'rgba(120, 120, 120, 0.1)', line: { width: 0 }, layer: 'below'
    })) : [], 
  [ignoredSegments, hideIgnoredData]);

  const baseLayout = useMemo(() => {
    const defaultRange = isReversed 
        ? [maxStation, minStation] 
        : [minStation, maxStation];

    return {
      margin: { l: 60, r: 20, t: 20, b: 40 },
      xaxis: { 
        title: isReversed ? 'Station (Reverse)' : 'Station',
        range: xAxisRange ? xAxisRange : defaultRange,
        rangeslider: { visible: false },
        
        showgrid: true,
        gridcolor: 'rgba(0,0,0,0.1)', 
        gridwidth: 1,
        griddash: 'dot', 
        zerolinecolor: 'rgba(0,0,0,0.1)',
        zerolinedash: 'dot',
        
        hoverformat: '.0f',

        tickmode: 'array', 
        tickvals: xTicks.vals,
        ticktext: xTicks.text,
        tickformat: '' 
      },
      showlegend: false,
      dragmode: 'zoom',
      hovermode: 'x unified',
      shapes: ignoredShapes,
      
      font: { family: 'Inter, sans-serif' },
      hoverlabel: {
        bgcolor: 'rgba(255, 255, 255, 0.95)',
        font: { color: '#1f2937' },
        bordercolor: '#e5e7eb'
      }
    };
  }, [minStation, maxStation, ignoredShapes, xAxisRange, xTicks, isReversed]);

  const iriLayout = useMemo(() => ({
    ...baseLayout,
    yaxis: { 
      title: `IRI (${iriUnit})`, 
      fixedrange: true, 
      range: iriYRange,
      showgrid: true,
      gridcolor: 'rgba(0,0,0,0.1)',
      gridwidth: 1,
      griddash: 'dot',
      zerolinecolor: 'rgba(0,0,0,0.1)',
      zerolinedash: 'dot',
    }
  }), [baseLayout, iriUnit, iriYRange]);

  const elevLayout = useMemo(() => ({
    ...baseLayout,
    yaxis: { 
      title: `Deviation (${elevUnit})`, 
      fixedrange: true, 
      range: elevYRange,
      showgrid: true,
      gridcolor: 'rgba(0,0,0,0.1)',
      gridwidth: 1,
      griddash: 'dot',
      zerolinecolor: 'rgba(0,0,0,0.1)',
      zerolinedash: 'dot',
    },
    annotations: [
      { x: 0.02, y: 0, xref: 'paper', yref: 'y', text: `${cutoffWavelength}' Filter`, showarrow: false, xanchor: 'left', font: { color: '#9ca3af' } }
    ]
  }), [baseLayout, elevUnit, cutoffWavelength, elevYRange]);


  // --- Throttled Relayout Handler ---
  const handleRelayout = useCallback((eventData) => {
    if (throttleRef.current) return; 

    let range = null;
    if (eventData['xaxis.range[0]'] && eventData['xaxis.range[1]']) {
      range = [eventData['xaxis.range[0]'], eventData['xaxis.range[1]']];
    } else if (eventData['xaxis.range']) {
      range = eventData['xaxis.range'];
    } else if (eventData['xaxis.autorange']) {
      range = isReversed 
        ? [maxStation, minStation] 
        : [minStation, maxStation];
    }
    
    if(range) {
        throttleRef.current = setTimeout(() => {
            setXAxisRange(range);
            throttleRef.current = null;
        }, 50);
    }
  }, [minStation, maxStation, isReversed]);

  if (kmlLoading) return <div className="p-4 text-center text-gray-500">Loading Charts...</div>;
  if (kmlError) return <div className="p-4 text-center text-red-500">Error: {kmlError}</div>;
  if (!iriProfileData && !elevationProfileData) return <div className="p-4 text-center text-gray-500">No data available.</div>;

  const unitOptions = { metric: 'Metric (m/km)', standard: 'Standard (in/mi)', raw: 'Raw' };

  return (
    <div className="bg-white ">
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3 h-20">
         <div>
            <h3 className="text-lg font-medium text-gray-900">Chart Analysis</h3>
           <p className="text-sm text-gray-500 mt-0.5">
              {iriProfileData?.summary?.totalPoints?.toLocaleString()} points
            </p>
         </div>
         <div className="flex items-center gap-3">
            <div className="flex items-center border border-gray-300 rounded overflow-hidden bg-white">
              <button onClick={() => setWheelpathVisibility(p => ({...p, left: !p.left}))} className={`px-3 py-1.5 text-xs font-medium ${wheelpathVisibility.left ? 'bg-green-100 text-green-800' : 'text-gray-600 hover:bg-gray-50'}`}>L</button>
              <button onClick={() => setWheelpathVisibility(p => ({...p, right: !p.right}))} className={`px-3 py-1.5 text-xs font-medium border-l border-r ${wheelpathVisibility.right ? 'bg-blue-100 text-blue-800' : 'text-gray-600 hover:bg-gray-50'}`}>R</button>
              <button onClick={() => setWheelpathVisibility(p => ({...p, avg: !p.avg}))} className={`px-3 py-1.5 text-xs font-medium ${wheelpathVisibility.avg ? 'bg-purple-100 text-purple-800' : 'text-gray-600 hover:bg-gray-50'}`}>AVG</button>
            </div>
            <button onClick={() => setShowAlrRegions(!showAlrRegions)} className={`px-3 py-1.5 text-xs rounded border font-medium ${showAlrRegions ? 'bg-purple-100 text-purple-700 border-purple-300' : 'bg-gray-100 text-gray-600 border-gray-300'}`}>ALR</button>
            <button onClick={() => setShowSurfaceEvents(!showSurfaceEvents)} className={`px-3 py-1.5 text-xs rounded border font-medium ${showSurfaceEvents ? 'bg-blue-100 text-blue-700 border-blue-300' : 'bg-gray-100 text-gray-600 border-gray-300'}`}>Events</button>
            <button onClick={() => setHideIgnoredData(!hideIgnoredData)} className={`px-3 py-1.5 text-xs rounded border font-medium ${hideIgnoredData ? 'bg-yellow-100 text-yellow-800 border-yellow-300' : 'bg-gray-100 text-gray-600 border-gray-300'}`}>Hide Gaps</button>
            <div className="relative">
              <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="flex items-center justify-between w-36 px-2.5 py-1.5 text-xs rounded border bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200">
                <span>{unitOptions[units]}</span>
                <ChevronDownIcon className="w-3 h-3 ml-1" />
              </button>
              {isDropdownOpen && (
                <div className="absolute right-0 mt-1 w-36 bg-white rounded border shadow-lg z-20">
                  {Object.keys(unitOptions).map(k => (
                    <div key={k} onClick={() => { setUnits(k); setIsDropdownOpen(false); }} className="px-3 py-2 text-xs hover:bg-gray-100 cursor-pointer text-gray-700">
                      {unitOptions[k]}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button 
              onClick={() => exportToCSV(combinedCSVData, `profile_${units}.csv`, units, { iriFactor, elevFactor })}
              className="flex items-center px-2.5 py-1.5 text-xs rounded border bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200"
            >
              <DocumentArrowDownIcon className="w-3.5 h-3.5 mr-1" /> CSV
            </button>
            {/* <div className="mt-auto">
              <FieldExportButton
  iriProfileData={iriProfileData}
  elevationProfileData={elevationProfileData}
  sessionData={sessionData}
  alrDefects={alrDefects}
  surfaceEvents={surfaceEvents}
  thresholds={thresholds}
  
/>
                </div> */}
         </div>
      </div>

      <div className="p-1">
        {iriProfileData && (
          <div className="h-[500px] w-full mb-2 relative">
             <FastPlotlyChart 
               divId="iri-chart"
               data={iriData}
               layout={iriLayout}
               config={{ 
                  responsive: true, 
                  displayModeBar: 'hover', 
                  scrollZoom: true,
                  modeBarButtonsToRemove: ['lasso2d', 'select2d'] 
               }}
               setPlotInstance={(p) => { iriChartRef.current = p; }}
               onRelayout={handleRelayout}
             />
          </div>
        )}

        {filteredElev && (
          <div className="h-[400px] w-full relative">
             <FastPlotlyChart 
               divId="elev-chart"
               data={elevData}
               layout={elevLayout}
               config={{ 
                  responsive: true, 
                  displayModeBar: 'hover', 
                  scrollZoom: true,
                  modeBarButtonsToRemove: ['lasso2d', 'select2d'] 
               }}
               setPlotInstance={(p) => { elevChartRef.current = p; }}
               onRelayout={handleRelayout}
             />
          </div>
        )}
      </div>
    </div>
  );
};

export default KmlCharts;